import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { MainContentArea } from '@/components/layout/MainContentArea'
import { SearchResultItem } from '@/components/search/SearchResultItem'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useAppStore } from '@/store/appStore'
import { clientSearch } from '@/utils/clientSearch'
import { trackEvent } from '@/services/appInsights'

export function SearchResultsPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') ?? ''
  const { data: bundle } = useContentBundle()
  const { selectedModelViewId, selectedLevelId, selectedContentTagIds, searchFilteredOnly } = useAppStore()
  const navigate = useNavigate()

  const searchContentTagId =
    searchFilteredOnly && selectedContentTagIds.length === 1 && !selectedContentTagIds.includes(-1)
      ? selectedContentTagIds[0]
      : undefined

  const isLoading = !bundle

  const results = bundle && query.length >= 2
    ? clientSearch(bundle, query, 100, searchFilteredOnly ? {
        modelViewId: selectedModelViewId ?? undefined,
        levelId: selectedLevelId ?? undefined,
        contentTagId: searchContentTagId,
      } : undefined)
    : []

  // One event per submitted query. The ref stops re-renders (bundle refresh, filter store
  // updates) from recounting a search the user did not repeat.
  const trackedQuery = useRef<string | null>(null)
  const resultCount = results.length
  useEffect(() => {
    if (isLoading || query.length < 2 || trackedQuery.current === query) return
    trackedQuery.current = query
    trackEvent('Search Performed', {
      term: query,
      resultCount,
      ...(selectedModelViewId != null ? { modelViewId: selectedModelViewId } : {}),
    })
  }, [isLoading, query, resultCount, selectedModelViewId])

  return (
    <MainContentArea>
      <div className="content-scroll-area" tabIndex={0}>
          <h1 className="page-title">
            {query ? t('search.resultsForQuery', { query }) : t('search.resultsFor')}
          </h1>

          {query.length < 2 && (
            <p className="empty-state">{t('search.minChars')}</p>
          )}

          {query.length >= 2 && isLoading && (
            <div className="loading-state">{t('search.loading')}</div>
          )}

          {query.length >= 2 && results.length === 0 && !isLoading && (
            <p className="empty-state">{t('search.noResultsForQuery', { query })}</p>
          )}

          {results.length > 0 && (
            <div className="search-results-list">
              <p className="search-results-count">
                {t('search.resultCount', { count: results.length })}
              </p>
              {results.map((result, idx) => (
                <SearchResultItem
                  key={idx}
                  result={result}
                  query={query}
                  bundle={bundle}
                  onNavigate={(path) => navigate(path)}
                />
              ))}
            </div>
          )}
      </div>
    </MainContentArea>
  )
}
